type LogLevel = 'error' | 'warn' | 'info' | 'debug';

type ClientLogEvent = {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: unknown;
  stack?: string;
  url?: string;
  userAgent?: string;
  appName?: string;
  appVersion?: string;
  env?: string;
  sessionId?: string;
  correlationId?: string;
};

const getObjectStringProp = (value: unknown, key: string): string | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const record = value as Record<string, unknown>;
  const prop = record[key];
  return typeof prop === 'string' ? prop : undefined;
};

const sanitizeForJson = (value: unknown, maxDepth = 6, seen = new WeakSet<object>()): unknown => {
  if (value === null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'undefined') return null;
  if (typeof value === 'function') return '[function]';
  if (typeof value === 'symbol') return value.toString();

  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }

  if (maxDepth <= 0) return '[max-depth]';

  if (Array.isArray(value)) {
    return value.map(v => sanitizeForJson(v, maxDepth - 1, seen));
  }

  if (typeof value === 'object') {
    const obj = value as object;
    if (seen.has(obj)) return '[circular]';
    seen.add(obj);

    const record = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(record)) {
      out[k] = sanitizeForJson(v, maxDepth - 1, seen);
    }
    return out;
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return '[unserializable]';
  }
};

const getChatAccessToken = (): string | null => {
  try {
    const raw = localStorage.getItem('oquis-auth-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { token?: string | null } };
    const token = parsed?.state?.token;
    return typeof token === 'string' && token.length > 0 ? token : null;
  } catch {
    return null;
  }
};

export function initClientLogging(options: {
  appName: string;
  appVersion?: string;
  env?: string;
  sessionId?: string;
}): void {
  const globalFlag = '__oquis_client_logging_initialized__';
  if ((window as unknown as Record<string, unknown>)[globalFlag]) return;

  const metaEnv = import.meta.env as unknown as {
    VITE_LOGGING_ENDPOINT?: string;
    VITE_AUTH_SERVER_URL?: string;
  };

  const endpoint =
    metaEnv.VITE_LOGGING_ENDPOINT ||
    (metaEnv.VITE_AUTH_SERVER_URL ? `${metaEnv.VITE_AUTH_SERVER_URL}/api/v1/client-logs` : undefined);

  if (!endpoint) return;

  (window as unknown as Record<string, unknown>)[globalFlag] = true;

  const env = options.env ?? (import.meta.env.MODE || 'development');
  const appName = options.appName;
  const appVersion = options.appVersion;
  const sessionId = options.sessionId;

  const queue: ClientLogEvent[] = [];
  let flushTimer: number | undefined;

  const flush = (): void => {
    if (queue.length === 0) return;
    const events = queue.splice(0, 50);

    const token = getChatAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const payload = {
      appName,
      appVersion,
      env,
      sessionId,
      events,
    };

    // Best-effort; do not block UX
    fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => undefined);
  };

  const enqueue = (level: LogLevel, message: string, context?: unknown, stack?: string): void => {
    queue.push({
      timestamp: new Date().toISOString(),
      level,
      message,
      context: context === undefined ? undefined : sanitizeForJson(context),
      stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      appName,
      appVersion,
      env,
      sessionId,
    });

    if (!flushTimer) {
      flushTimer = window.setTimeout(() => {
        flushTimer = undefined;
        flush();
      }, 2000);
    }
  };

  const wrapConsole = (level: LogLevel, original: (...args: unknown[]) => void) => {
    return (...args: unknown[]) => {
      try {
        const msg = args
          .map(a => (typeof a === 'string' ? a : JSON.stringify(sanitizeForJson(a))))
          .join(' ');
        enqueue(level, msg, args);
      } catch {
        enqueue(level, '[unserializable console args]', args);
      }
      original(...args);
    };
  };

  // Patch console so existing console.log statements are captured in prod
  console.error = wrapConsole('error', console.error.bind(console));
  console.warn = wrapConsole('warn', console.warn.bind(console));
  console.info = wrapConsole('info', console.info.bind(console));
  console.log = wrapConsole('info', console.log.bind(console));
  console.debug = wrapConsole('debug', console.debug.bind(console));

  window.addEventListener('error', (event) => {
    const err = event.error as Error | undefined;
    const stack = err?.stack;
    enqueue('error', event.message || 'window.error', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    }, stack);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    const message = getObjectStringProp(reason, 'message') ?? 'unhandledrejection';
    const stack = getObjectStringProp(reason, 'stack');
    enqueue('error', message, { reason }, stack);
  });

  window.addEventListener('beforeunload', () => flush());

  // One-shot info so we can validate in DB
  enqueue('info', '[client-logging] enabled', { endpoint, env, appName });
}
